/**
 * Resolve the Apex class/trigger name a SAVE will run under.
 *
 * The editor tab name can be an artifact label ("auto_fix:Refactor …",
 * "RecordTypeHelper (original)") when a refactor diff was opened — saving that
 * trips `validate-save 400: invalid class name`. The authoritative name is the
 * one declared IN the source (what actually compiles); fall back to the
 * cleaned tab name.
 *
 * Extracted from useMetadataEditorPanel.ts so the resolution is unit-testable
 * without the panel's store/hook graph (same pattern as the folder's other
 * single-purpose modules).
 */
import { SF_EXTENSION_REGEX } from "@/utils/chat/metadataDetection";

/**
 * Strip the directory path from a file name: removes everything up to and
 * including the LAST `/` that occurs before the first newline (a `/` after a
 * newline is left intact).
 */
function _stripDirPath(fileName: string): string {
  const nl = fileName.indexOf("\n");
  const firstSegmentEnd = nl < 0 ? fileName.length : nl;
  const lastSlash = fileName.lastIndexOf("/", firstSegmentEnd - 1);
  return lastSlash < 0 ? fileName : fileName.slice(lastSlash + 1);
}

function _stripKnownMetadataExtension(fileName: string): string {
  // Uses the SHARED `SF_EXTENSION_REGEX` util (@/utils/chat/metadataDetection):
  // it strips MANY SF extensions
  // (cls|trigger|page|component|object|flow|email|xml|resource|app|layout|tab),
  // far more than the local `bareClassName` (cls/trigger/page only).
  return _stripDirPath(fileName).replace(SF_EXTENSION_REGEX, "");
}

// Apex identifier helpers.
function _isApexLetter(ch: string): boolean {
  return (ch >= "a" && ch <= "z") || (ch >= "A" && ch <= "Z");
}
function _isApexIdentChar(ch: string): boolean {
  return _isApexLetter(ch) || (ch >= "0" && ch <= "9") || ch === "_";
}
/** Backend rule the SAVE endpoint enforces: starts with a letter, alnum/underscore
 *  only, ≤80 chars. */
function _isValidApexName(name: string): boolean {
  if (!name || name.length > 80 || !_isApexLetter(name[0])) return false;
  for (const ch of name) if (!_isApexIdentChar(ch)) return false;
  return true;
}

/**
 * Apex reserved words that can never be a class/trigger name (case-insensitive).
 * Guard for the extractor: with comments/strings stripped, VALID Apex cannot
 * put one of these after `class` — but the editor often holds invalid code
 * (that is exactly when the user hits Save), and a junk extraction must LOSE
 * to the tab-name fallback, never win it.
 */
const APEX_RESERVED_WORDS = new Set([
  "abstract", "and", "break", "catch", "class", "continue", "delete", "do",
  "else", "enum", "extends", "false", "final", "finally", "for", "from",
  "global", "if", "implements", "insert", "instanceof", "interface", "like",
  "list", "map", "merge", "new", "not", "null", "on", "or", "override",
  "private", "protected", "public", "return", "select", "set", "sharing",
  "static", "super", "testmethod", "this", "throw", "transient", "trigger",
  "true", "try", "undelete", "update", "upsert", "virtual", "void",
  "webservice", "when", "where", "while", "with", "without",
]);

/**
 * Blank out comments and string literals (Apex strings are single-quoted) so
 * keyword scans can never match inside them. WHY: a doc-comment phrase such as
 * "Helper class for CoTravel triggers" made the extractor return `for` — prod
 * saves on 2026-08-19 ran validate-save under class names `for` and `Name`
 * taken from header comments, so the compile gate checked the code under the
 * wrong filename and cache invalidation keyed on a name that doesn't exist.
 * Replaces stripped chars with spaces so indices stay aligned.
 */
export function stripApexCommentsAndStrings(source: string): string {
  const out = source.split("");
  let i = 0;
  while (i < source.length) {
    const ch = source[i];
    const next = i + 1 < source.length ? source[i + 1] : "";
    if (ch === "/" && next === "/") {
      while (i < source.length && source[i] !== "\n") { out[i] = " "; i++; }
    } else if (ch === "/" && next === "*") {
      out[i] = " "; out[i + 1] = " "; i += 2;
      while (i < source.length && !(source[i] === "*" && source[i + 1] === "/")) {
        if (source[i] !== "\n") out[i] = " ";
        i++;
      }
      if (i < source.length) { out[i] = " "; out[i + 1] = " "; i += 2; }
    } else if (ch === "'") {
      out[i] = " "; i++;
      while (i < source.length && source[i] !== "'" && source[i] !== "\n") {
        // A backslash escapes the next char (\' must not close the literal).
        if (source[i] === "\\") { out[i] = " "; i++; if (i < source.length && source[i] !== "\n") { out[i] = " "; i++; } continue; }
        out[i] = " "; i++;
      }
      if (i < source.length && source[i] === "'") { out[i] = " "; i++; }
    } else {
      i++;
    }
  }
  return out.join("");
}

/** The identifier following the first whole-word `keyword` ("class"/"trigger"). */
function _identifierAfterKeyword(source: string, keyword: string): string | undefined {
  const lower = source.toLowerCase();
  let from = 0;
  for (;;) {
    const idx = lower.indexOf(keyword, from);
    if (idx < 0) return undefined;
    const before = idx === 0 ? " " : source[idx - 1];
    const afterIdx = idx + keyword.length;
    const after = afterIdx < source.length ? source[afterIdx] : " ";
    // whole-word: neighbours must not be identifier chars
    if (!_isApexIdentChar(before) && !_isApexIdentChar(after)) {
      let i = afterIdx;
      while (i < source.length && (source[i] === " " || source[i] === "\t" ||
                                   source[i] === "\n" || source[i] === "\r")) i++;
      let j = i;
      while (j < source.length && _isApexIdentChar(source[j])) j++;
      const name = source.slice(i, j);
      if (name) return name;
    }
    from = afterIdx;
  }
}

/** Resolve the class name the SAVE endpoint will accept — declaration in the
 *  source wins (scanned with comments/strings blanked and reserved words
 *  rejected), cleaned tab name is the fallback. */
export function resolveApexClassName(tabName: string, source?: string): string {
  const scannable = source ? stripApexCommentsAndStrings(source) : undefined;
  const fromSource = scannable
    ? (_identifierAfterKeyword(scannable, "class") ?? _identifierAfterKeyword(scannable, "trigger"))
    : undefined;
  if (
    fromSource &&
    _isValidApexName(fromSource) &&
    !APEX_RESERVED_WORDS.has(fromSource.toLowerCase())
  ) {
    return fromSource;
  }
  return _stripKnownMetadataExtension(tabName || "");
}
